import { useMemo, useState } from "react";
import { Loader2, Download, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/auth";

const GRADES = ["Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12", "Career Professional"];
const PLANS = ["Domestic", "International", "Job Seeker"];
const inputCls = "w-full rounded-xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35";
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/45 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl bg-paper border border-ink/10 p-6 shadow-2xl" data-testid={testid} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg tracking-tight">{title}</h3>
          <button onClick={onClose} data-testid={`${testid}-close`} className="p-1.5 rounded-full hover:bg-ink/5"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-4 space-y-3">{children}</div>
      </div>
    </div>
  );
}

function BulkBar({ count, onDownload, onDelete, busy, testid, downloadLabel }) {
  if (count === 0) return null;
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-ink text-paper px-5 py-3" data-testid={testid}>
      <span className="text-xs font-semibold">{count} selected</span>
      <div className="flex-1" />
      <button onClick={onDownload} disabled={busy} data-testid={`${testid}-download`} className="flex items-center gap-1.5 text-xs font-semibold bg-iqcyan text-ink px-4 py-2 rounded-full hover:brightness-110 disabled:opacity-50 transition">
        <Download className="h-3.5 w-3.5" /> {downloadLabel}
      </button>
      <button onClick={onDelete} disabled={busy} data-testid={`${testid}-delete`} className="flex items-center gap-1.5 text-xs font-semibold bg-iqpink text-paper px-4 py-2 rounded-full hover:brightness-110 disabled:opacity-50 transition">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Delete
      </button>
    </div>
  );
}

async function saveBlob(res, filename) {
  if (!res.ok) throw new Error("download failed");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function UsersManager({ users, onChanged }) {
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => [u.name, u.email, u.mobile, u.school].some((v) => (v || "").toLowerCase().includes(q)));
  }, [users, search]);

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allChecked = filtered.length > 0 && filtered.every((u) => sel.has(u.id));
  const toggleAll = () => setSel(allChecked ? new Set() : new Set(filtered.map((u) => u.id)));

  const del = async (ids) => {
    if (!window.confirm(`Delete ${ids.length} user${ids.length === 1 ? "" : "s"} and all their data? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const res = ids.length === 1
        ? await authFetch(`/api/admin/users/${ids[0]}`, { method: "DELETE" })
        : await authFetch("/api/admin/users/bulk", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids, action: "delete" }) });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Delete failed");
      toast.success(ids.length === 1 ? "User deleted" : `${d.deleted} users deleted`);
      setSel(new Set());
      onChanged();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const downloadCsv = async (ids) => {
    try {
      const res = await authFetch(`/api/admin/users.csv?ids=${ids.join(",")}`);
      await saveBlob(res, ids.length === 1 ? "brightiq-user.csv" : "brightiq-users.csv");
      toast.success("CSV downloaded");
    } catch { toast.error("Download failed"); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const { name, mobile, grade, school, study_location, role } = editing;
      const res = await authFetch(`/api/admin/users/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name, mobile, grade, school, study_location, role }) });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Update failed");
      toast.success("User updated");
      setEditing(null);
      onChanged();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="rounded-3xl bg-cream border border-ink/10 overflow-hidden" data-testid="users-manager">
      <div className="px-6 py-4 border-b border-ink/10 flex items-center gap-4">
        <span className="font-display font-bold text-sm tracking-tight">Registered users <span className="text-ink/40 font-body font-medium">({filtered.length})</span></span>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, mobile…" data-testid="users-search" className="ml-auto rounded-full border border-ink/15 bg-white px-4 py-1.5 text-xs outline-none focus:border-iqblue w-56" />
      </div>
      <div className="px-4 pt-4"><BulkBar count={sel.size} busy={busy} testid="users-bulk-bar" downloadLabel="Download CSV" onDownload={() => downloadCsv([...sel])} onDelete={() => del([...sel])} /></div>
      <div className="overflow-x-auto mt-4">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-ink/45 uppercase tracking-wider">
              <th className="pl-6 pr-2 py-3 w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="users-check-all" className="h-3.5 w-3.5 accent-iqblue" /></th>
              {["Name", "Email", "Mobile", "Grade", "Role", "Joined", ""].map((c) => <th key={c} className="px-3 py-3 font-semibold">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map((u, i) => (
              <tr key={u.id} className="border-t border-ink/5 hover:bg-white/60 transition-colors" data-testid={`user-row-${i}`}>
                <td className="pl-6 pr-2 py-3"><input type="checkbox" checked={sel.has(u.id)} onChange={() => toggle(u.id)} data-testid={`user-check-${i}`} className="h-3.5 w-3.5 accent-iqblue" /></td>
                <td className="px-3 py-3 font-semibold text-ink/85 whitespace-nowrap">{u.name}</td>
                <td className="px-3 py-3 whitespace-nowrap text-ink/75">{u.email}</td>
                <td className="px-3 py-3 whitespace-nowrap text-ink/75">{u.mobile || "—"}</td>
                <td className="px-3 py-3 whitespace-nowrap text-ink/75">{u.grade || "—"}</td>
                <td className="px-3 py-3 whitespace-nowrap"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${u.role === "admin" ? "bg-iqpurple/15 text-iqpurple" : "bg-ink/5 text-ink/60"}`}>{u.role}</span></td>
                <td className="px-3 py-3 whitespace-nowrap text-ink/55">{fmtDate(u.created_at)}</td>
                <td className="px-3 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <button onClick={() => downloadCsv([u.id])} title="Download CSV" data-testid={`user-download-${i}`} className="p-1.5 rounded-lg hover:bg-iqcyan/15 text-ink/50 hover:text-iqcyan transition-colors"><Download className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setEditing({ ...u })} title="Edit" data-testid={`user-edit-${i}`} className="p-1.5 rounded-lg hover:bg-iqblue/10 text-ink/50 hover:text-iqblue transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del([u.id])} title="Delete" data-testid={`user-delete-${i}`} className="p-1.5 rounded-lg hover:bg-iqpink/10 text-ink/50 hover:text-iqpink transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="px-6 py-6 text-ink/40">Nothing here yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <Modal title={`Edit — ${editing.email}`} testid="user-edit-modal" onClose={() => setEditing(null)}>
          <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Full name" data-testid="user-edit-name" className={inputCls} />
          <input value={editing.mobile || ""} onChange={(e) => setEditing({ ...editing, mobile: e.target.value })} placeholder="Mobile" data-testid="user-edit-mobile" className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <select value={editing.grade || GRADES[0]} onChange={(e) => setEditing({ ...editing, grade: e.target.value })} data-testid="user-edit-grade" className={inputCls}>{GRADES.map((g) => <option key={g}>{g}</option>)}</select>
            <select value={editing.study_location || PLANS[0]} onChange={(e) => setEditing({ ...editing, study_location: e.target.value })} data-testid="user-edit-plan" className={inputCls}>{PLANS.map((p) => <option key={p}>{p}</option>)}</select>
          </div>
          <input value={editing.school || ""} onChange={(e) => setEditing({ ...editing, school: e.target.value })} placeholder="School / College" data-testid="user-edit-school" className={inputCls} />
          <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} data-testid="user-edit-role" className={inputCls}>
            <option value="user">user</option>
            <option value="admin">admin</option>
          </select>
          <button onClick={saveEdit} disabled={busy} data-testid="user-edit-save" className="w-full flex items-center justify-center gap-2 bg-ink text-paper font-semibold py-3 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </button>
        </Modal>
      )}
    </div>
  );
}

export function ResultsManager({ reports, onChanged }) {
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(new Set());
  const [renaming, setRenaming] = useState(null);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter((r) => [r.name, r.email, r.persona].some((v) => (v || "").toLowerCase().includes(q)));
  }, [reports, search]);

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allChecked = filtered.length > 0 && filtered.every((r) => sel.has(r.id));
  const toggleAll = () => setSel(allChecked ? new Set() : new Set(filtered.map((r) => r.id)));
  const items = (ids) => ids.map((id) => ({ kind: "classic", id }));

  const del = async (ids) => {
    if (!window.confirm(`Delete ${ids.length} report${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const res = ids.length === 1
        ? await authFetch(`/api/admin/reports/classic/${ids[0]}`, { method: "DELETE" })
        : await authFetch("/api/admin/reports/bulk", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items: items(ids), action: "delete" }) });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Delete failed");
      toast.success(ids.length === 1 ? "Report deleted" : `${d.deleted} reports deleted`);
      setSel(new Set());
      onChanged();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const downloadOne = async (r) => {
    try {
      const res = await authFetch(`/api/reports/${r.id}/pdf${r.lang ? `?lang=${r.lang}` : ""}`);
      await saveBlob(res, `brightiq-${(r.name || "report").toLowerCase().replace(/\s+/g, "-")}.pdf`);
      toast.success("PDF downloaded");
    } catch { toast.error("Download failed"); }
  };

  const downloadZip = async (ids) => {
    setBusy(true);
    try {
      const res = await authFetch("/api/admin/reports/download", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items: items(ids), action: "download" }) });
      await saveBlob(res, "brightiq-reports.zip");
      toast.success("ZIP downloaded");
    } catch { toast.error("Download failed"); }
    setBusy(false);
  };

  const saveRename = async () => {
    setBusy(true);
    try {
      const res = await authFetch(`/api/admin/reports/classic/${renaming.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: renaming.name }) });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Rename failed");
      toast.success("Report renamed");
      setRenaming(null);
      onChanged();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="rounded-3xl bg-cream border border-ink/10 overflow-hidden" data-testid="results-manager">
      <div className="px-6 py-4 border-b border-ink/10 flex items-center gap-4">
        <span className="font-display font-bold text-sm tracking-tight">Full battery reports <span className="text-ink/40 font-body font-medium">({filtered.length})</span></span>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, persona…" data-testid="results-search" className="ml-auto rounded-full border border-ink/15 bg-white px-4 py-1.5 text-xs outline-none focus:border-iqblue w-56" />
      </div>
      <div className="px-4 pt-4"><BulkBar count={sel.size} busy={busy} testid="results-bulk-bar" downloadLabel="Download ZIP" onDownload={() => downloadZip([...sel])} onDelete={() => del([...sel])} /></div>
      <div className="overflow-x-auto mt-4">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-ink/45 uppercase tracking-wider">
              <th className="pl-6 pr-2 py-3 w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="results-check-all" className="h-3.5 w-3.5 accent-iqblue" /></th>
              {["Name", "Email", "Track", "Persona", "Aptitude", "When", ""].map((c) => <th key={c} className="px-3 py-3 font-semibold">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.id} className="border-t border-ink/5 hover:bg-white/60 transition-colors" data-testid={`result-row-${i}`}>
                <td className="pl-6 pr-2 py-3"><input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} data-testid={`result-check-${i}`} className="h-3.5 w-3.5 accent-iqblue" /></td>
                <td className="px-3 py-3 font-semibold text-ink/85 whitespace-nowrap">{r.name}</td>
                <td className="px-3 py-3 whitespace-nowrap text-ink/75">{r.email}</td>
                <td className="px-3 py-3 whitespace-nowrap text-ink/75">{(r.track || "student") === "professional" ? "Professional" : "Student"}</td>
                <td className="px-3 py-3 whitespace-nowrap text-ink/75">{r.persona}</td>
                <td className="px-3 py-3 whitespace-nowrap font-bold text-ink/80">{r.aptPct}%</td>
                <td className="px-3 py-3 whitespace-nowrap text-ink/55">{fmtDate(r.created_at)}</td>
                <td className="px-3 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <button onClick={() => downloadOne(r)} title="Download PDF" data-testid={`result-download-${i}`} className="p-1.5 rounded-lg hover:bg-iqcyan/15 text-ink/50 hover:text-iqcyan transition-colors"><Download className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setRenaming({ ...r })} title="Rename" data-testid={`result-edit-${i}`} className="p-1.5 rounded-lg hover:bg-iqblue/10 text-ink/50 hover:text-iqblue transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del([r.id])} title="Delete" data-testid={`result-delete-${i}`} className="p-1.5 rounded-lg hover:bg-iqpink/10 text-ink/50 hover:text-iqpink transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="px-6 py-6 text-ink/40">Nothing here yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {renaming && (
        <Modal title="Rename report" testid="result-rename-modal" onClose={() => setRenaming(null)}>
          <p className="text-xs text-ink/50">Changes the name shown on the report cover, dashboard and PDF.</p>
          <input value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} placeholder="Name on report" data-testid="result-rename-input" className={inputCls} />
          <button onClick={saveRename} disabled={busy} data-testid="result-rename-save" className="w-full flex items-center justify-center gap-2 bg-ink text-paper font-semibold py-3 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save name"}
          </button>
        </Modal>
      )}
    </div>
  );
}
